/**
 * Pure view model and copy for the Danger zone's `Close this pool` control.
 *
 * This module is pure: no React import, no DOM, no fetch. It exists because
 * the one interesting decision on this control is a rule, not a rendering:
 * `POST /pool/archive` accepts the close only while the Commissioner is the
 * pool's ONLY member, counting pending requests and suspended members as
 * members. The client re-states that rule here so a fan reads why the control
 * is unavailable instead of tapping it and collecting a CONFLICT.
 *
 * The client re-stating a server rule is a duplicate by definition, and this
 * one is knowingly incomplete: `POST /pool/get` reports activeMemberCount and
 * pendingMemberCount and nothing else, so a SUSPENDED member is invisible
 * here while the server still counts them. A pool holding one suspended
 * member therefore shows a live control and is refused on tap, which
 * `showApiError` renders like any other refusal. That case is rare (a
 * Commissioner alone with somebody they suspended rather than removed) and
 * costs a fan one error modal. Closing that gap means adding a
 * suspendedMemberCount to the /pool/get contract, which is a change to the
 * single read every settings card depends on, and it belongs to its own card
 * rather than riding along with this one.
 *
 * Nothing here ever decides that a close is ALLOWED. The server refuses on
 * its own rule, so the worst this module can do is disable a control that
 * would have worked, or let one refusal through.
 */

export const CLOSE_POOL_LABEL = "Close this pool";

export const CLOSE_POOL_HINT_READY =
  "You're the only one in this pool, so you can close it and it leaves your Pools list.";

export const CLOSE_POOL_HINT_BLOCKED =
  "You can only close a pool that nobody else is in. Hand the pool over instead, or wait until the others have left.";

export const CLOSE_POOL_HINT_UNKNOWN =
  "We could not check who else is in this pool, so closing is unavailable right now.";

export const CLOSE_POOL_CONFIRM_TITLE = "Close this pool?";

export const CLOSE_POOL_CONFIRM_BODY =
  "The pool leaves your Pools list, its invite code stops working, and nobody can join it again. You're its only member, so nobody else loses picks. You can't undo this yourself.";

export const CLOSE_POOL_CONFIRM_LABEL = "Close pool";

export type typeClosePoolState = {
  /**
   * ready: the caller is alone in the pool and the control is live.
   * blocked: somebody else is in the pool, in any membership status.
   * unknown: the counts have not been read, so nothing can be claimed.
   */
  phase: "ready" | "blocked" | "unknown";
  hint: string;
};

export function resolveClosePoolState(input: {
  activeMemberCount: number | null;
  pendingMemberCount: number | null;
}): typeClosePoolState {
  if (input.activeMemberCount === null || input.pendingMemberCount === null) {
    return { phase: "unknown", hint: CLOSE_POOL_HINT_UNKNOWN };
  }
  // The caller is always one of the active members, so "alone" is a count of
  // exactly one, never zero. A zero would mean the roster disagrees with the
  // session that just read it, which is a state to refuse rather than to treat
  // as the emptiest possible pool.
  if (input.activeMemberCount === 1 && input.pendingMemberCount === 0) {
    return { phase: "ready", hint: CLOSE_POOL_HINT_READY };
  }
  return { phase: "blocked", hint: CLOSE_POOL_HINT_BLOCKED };
}
